"use client";

import { useEffect, useState } from "react";
import { Users, TrendingUp, Eye, FileText, BarChart3 } from "lucide-react";

interface Stats {
  total: number;
  today: number;
  yesterday: number;
  weekly: { day: string; count: number }[];
  topDiseases: { page: string; count: number }[];
  topPapers: { page: string; count: number }[];
}

// 질환 slug → 한글 이름 매핑 (path에서 추출)
function formatPageName(page: string): string {
  const parts = page.split("/").filter(Boolean);
  if (parts.length >= 2) {
    return parts[1]
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return page;
}

function formatDay(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const days = ["일", "월", "화", "수", "목", "금", "토"];
  return days[d.getDay()] || "";
}

function MiniBarChart({ data }: { data: { day: string; count: number }[] }) {
  const FIXED_MAX = 30;
  const dynamicMax = Math.max(...data.map((d) => d.count), FIXED_MAX);
  return (
    <div>
      <div className="flex items-end gap-1 h-14">
        {data.map((d) => (
          <div
            key={d.day}
            className={`flex-1 rounded-sm transition-colors ${d.count > 0 ? "bg-primary/40 hover:bg-primary/60" : "bg-muted"}`}
            style={{ height: `${Math.max((d.count / dynamicMax) * 100, d.count > 0 ? 6 : 0)}%` }}
            title={`${d.day}: ${d.count}명`}
          />
        ))}
      </div>
      <div className="flex gap-1 mt-1">
        {data.map((d) => (
          <span key={d.day} className="flex-1 text-center text-[9px] text-muted-foreground/60">
            {formatDay(d.day)}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SiteStats() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    fetch("/api/visits")
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  if (!stats) return null;

  return (
    <div className="rounded-xl border bg-card/80 backdrop-blur p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">방문 통계</span>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <p className="text-lg font-bold">{stats.total.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">누적</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-primary">{stats.today}</p>
          <p className="text-[10px] text-muted-foreground">오늘</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-muted-foreground">{stats.yesterday}</p>
          <p className="text-[10px] text-muted-foreground">어제</p>
        </div>
      </div>

      {/* Weekly chart */}
      {stats.weekly.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] text-muted-foreground font-medium">주간 추이</p>
          <MiniBarChart data={stats.weekly} />
        </div>
      )}

      {/* Top diseases */}
      {stats.topDiseases.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <Eye className="h-3 w-3 text-clinical-blue" />
            <p className="text-[10px] text-muted-foreground font-medium">인기 질환</p>
          </div>
          <ul className="space-y-0.5">
            {stats.topDiseases.slice(0, 5).map((d, i) => (
              <li key={d.page} className="flex items-center justify-between text-[11px]">
                <span className="truncate text-muted-foreground">
                  <span className="text-muted-foreground/50 mr-1">{i + 1}.</span>
                  {formatPageName(d.page)}
                </span>
                <span className="text-[10px] text-muted-foreground/60 ml-1 shrink-0">{d.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  );
}
